import { useEffect, useRef, type ReactNode } from "react";
import type { TerminalMessage } from "../types";
import { useTerminalClasses } from "../theme/context";
import { cx } from "../util/cx";

export interface MessageListProps {
  messages: TerminalMessage[];
  renderMessage?: (message: TerminalMessage) => ReactNode;
  empty?: () => ReactNode;
}

export function MessageList({ messages, renderMessage, empty }: MessageListProps) {
  const classes = useTerminalClasses();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  if (!messages.length && empty) {
    return <div className={classes.messageList}>{empty()}</div>;
  }

  return (
    <div className={classes.messageList} role="log" aria-live="polite">
      {messages.map((m) =>
        renderMessage ? (
          <div key={m.id}>{renderMessage(m)}</div>
        ) : (
          <div
            key={m.id}
            className={cx(
              classes.message,
              m.role === "user" && classes.messageUser,
              m.role === "assistant" && classes.messageAssistant,
              m.role === "system" && classes.messageSystem,
              m.error && classes.messageError,
            )}
            data-role={m.role}
          >
            <span className="rt-message-content">{m.content}</span>
            {m.streaming && <span className="rt-caret" aria-hidden="true" />}
          </div>
        ),
      )}
      <div ref={endRef} />
    </div>
  );
}
